import { Track } from './track.js';
import {
  ShipState,
  InputFrame,
  emptyInput,
  copyInput,
  stepShip,
  resolveShipInteractions,
  ShipEventSink,
  StepContext,
} from './physics.js';
import { RaceProgress, computeStandings, StandingEntry } from './race.js';
import { BotController, BotDifficulty } from './bots.js';
import { getVehicle } from './vehicles.js';
import { TICK_DT, TICK_RATE, FINISH_TIMEOUT_TICKS } from './constants.js';
import { Vec3 } from './math.js';
import { makeFrame } from './spline.js';

export type RaceMode = 'race' | 'sprint' | 'timetrial' | 'elimination';

export interface WorldShipConfig {
  vehicleId: string;
  isBot: boolean;
  botDifficulty?: BotDifficulty;
  botSeed?: number;
}

export interface WorldConfig {
  track: Track;
  mode: RaceMode;
  laps: number;
  ships: WorldShipConfig[];
  /** Elimination: seconds between eliminations once armed. */
  eliminationInterval?: number;
}

export interface WorldEvents extends ShipEventSink {
  onCheckpoint?(shipIndex: number, checkpoint: number): void;
  onLap?(shipIndex: number, lap: number, lapTicks: number): void;
  onFinish?(shipIndex: number, finishTick: number): void;
  onEliminated?(shipIndex: number): void;
}

/**
 * The authoritative race simulation: N ships, fixed 60Hz timestep, deterministic given
 * the same inputs. Runs identically on the server, in offline play, and inside the
 * client's prediction loop (single-ship variant).
 */
export class RaceWorld {
  readonly track: Track;
  readonly mode: RaceMode;
  readonly laps: number;
  readonly ships: ShipState[] = [];
  readonly progress: RaceProgress[] = [];
  readonly inputs: InputFrame[] = [];
  private bots: (BotController | null)[] = [];
  private autopilots: (BotController | null)[] = [];
  tick = 0;
  standings: number[] = [];
  firstFinishTick = 0;
  raceOver = false;

  nextEliminationTick = 0;
  private eliminationIntervalTicks: number;

  events?: WorldEvents;
  private ctx: StepContext;

  constructor(cfg: WorldConfig) {
    this.track = cfg.track;
    this.mode = cfg.mode;
    this.laps = cfg.mode === 'sprint' ? 1 : cfg.laps;
    this.eliminationIntervalTicks = Math.round((cfg.eliminationInterval ?? 20) * TICK_RATE);

    const pose = { pos: new Vec3(), frame: makeFrame() };
    cfg.ships.forEach((sc, i) => {
      const vehicle = getVehicle(sc.vehicleId);
      const ship = new ShipState(i, vehicle.stats);
      this.track.spawnPose(i, pose);
      ship.pos.copy(pose.pos);
      ship.forward.copy(pose.frame.tangent);
      ship.up.copy(pose.frame.up);
      ship.splineId = 0;
      ship.s = 0;
      // resolve the spawn's actual s so progress starts correct
      const proj = {
        splineId: 0,
        s: 0,
        lateral: 0,
        height: 0,
        distSq: 0,
        progress: 0,
        width: 0,
      };
      this.track.project(ship.pos, undefined, proj);
      ship.s = proj.s;
      this.ships.push(ship);
      this.progress.push(new RaceProgress(this.track, this.laps, proj.progress));
      this.inputs.push(emptyInput());
      this.bots.push(
        sc.isBot ? new BotController(sc.botSeed ?? i * 7919 + 13, sc.botDifficulty ?? 'standard') : null
      );
      this.autopilots.push(null);
    });
    this.standings = this.ships.map((_, i) => i);
    this.ctx = { track: this.track, dt: TICK_DT, events: undefined };

    if (this.mode === 'elimination') {
      // first elimination after a grace period
      this.nextEliminationTick = TICK_RATE * 25;
    }
  }

  setEvents(ev: WorldEvents | undefined): void {
    this.events = ev;
    this.ctx.events = ev;
  }

  setInput(shipIndex: number, input: InputFrame): void {
    if (shipIndex >= 0 && shipIndex < this.inputs.length) copyInput(this.inputs[shipIndex], input);
  }

  activeShipCount(): number {
    let n = 0;
    for (const s of this.ships) if (!s.eliminated) n++;
    return n;
  }

  step(): void {
    this.tick++;

    for (let i = 0; i < this.ships.length; i++) {
      const ship = this.ships[i];
      if (ship.eliminated) continue;
      const prog = this.progress[i];

      // controller: bot, or autopilot for finished players
      const bot = this.bots[i];
      if (bot) {
        bot.drive(ship, this.ships, this.track, this.tick, this.inputs[i]);
      } else if (prog.finished) {
        let ap = this.autopilots[i];
        if (!ap) {
          ap = new BotController(i * 31 + 5, 'cruise');
          this.autopilots[i] = ap;
        }
        ap.drive(ship, this.ships, this.track, this.tick, this.inputs[i]);
        this.inputs[i].boost = false;
      }

      const rawProgress = stepShip(ship, this.inputs[i], this.ctx);
      const result = prog.update(rawProgress, this.tick, ship.respawnTimer > 0);
      ship.finished = prog.finished;

      if (result.checkpoint !== undefined) this.events?.onCheckpoint?.(i, result.checkpoint);
      if (result.lapDone) {
        if (prog.finished) {
          if (this.firstFinishTick === 0) this.firstFinishTick = this.tick;
          this.events?.onFinish?.(i, prog.finishTick);
        } else {
          this.events?.onLap?.(i, prog.lap, prog.lapTimes[prog.lapTimes.length - 1]);
        }
      }
    }

    if (this.mode !== 'timetrial') resolveShipInteractions(this.ships, this.ctx);

    // standings
    const entries: StandingEntry[] = this.ships.map((s, i) => ({
      shipIndex: i,
      finished: this.progress[i].finished,
      eliminated: s.eliminated,
      finishTick: this.progress[i].finishTick,
      total: this.progress[i].total,
    }));
    this.standings = computeStandings(entries);

    // elimination mode
    if (this.mode === 'elimination' && !this.raceOver && this.tick >= this.nextEliminationTick) {
      this.nextEliminationTick = this.tick + this.eliminationIntervalTicks;
      // eliminate the last-placed still-active, unfinished ship
      for (let k = this.standings.length - 1; k >= 0; k--) {
        const idx = this.standings[k];
        if (!this.ships[idx].eliminated && !this.progress[idx].finished) {
          if (this.activeShipCount() <= 1) break;
          this.ships[idx].eliminated = true;
          this.events?.onEliminated?.(idx);
          break;
        }
      }
      // last survivor wins
      const alive = this.ships.filter((s) => !s.eliminated && !s.finished);
      if (alive.length <= 1 && this.mode === 'elimination') {
        for (const s of alive) {
          const prog = this.progress[s.index];
          if (!prog.finished) {
            prog.finished = true;
            prog.finishTick = this.tick;
            s.finished = true;
            this.events?.onFinish?.(s.index, this.tick);
          }
        }
      }
    }

    // race end conditions
    if (!this.raceOver) {
      const allDone = this.ships.every((s, i) => this.progress[i].finished || s.eliminated);
      const timedOut =
        this.firstFinishTick > 0 && this.tick - this.firstFinishTick > FINISH_TIMEOUT_TICKS;
      if (allDone || timedOut) {
        this.raceOver = true;
        // DNF anyone still going
        for (let i = 0; i < this.ships.length; i++) {
          if (!this.progress[i].finished && !this.ships[i].eliminated) {
            this.ships[i].eliminated = true;
          }
        }
      }
    }
  }

  /** Step a single ship in isolation — used by client-side prediction. */
  stepSingle(shipIndex: number, input: InputFrame): void {
    const ship = this.ships[shipIndex];
    if (!ship || ship.eliminated) return;
    stepShip(ship, input, this.ctx);
  }
}
